// Begrippen uit het CRM die de schermen gebruiken (vroeger store.js en catalog.js).
import type { CrmRecord, FieldValue } from "./types";

/** Veldwaarde van een record. */
export const val = (record: CrmRecord | null | undefined, field: string): FieldValue | undefined => record?.fields[field];
export const str = (record: CrmRecord | null | undefined, field: string): string => {
  const v = val(record, field);
  return v === undefined ? "" : Array.isArray(v) ? v.join(", ") : String(v);
};
export const num = (record: CrmRecord | null | undefined, field: string): number | undefined => {
  const v = val(record, field);
  return typeof v === "number" ? v : undefined;
};
export const ids = (record: CrmRecord | null | undefined, field: string): string[] => {
  const v = val(record, field);
  return Array.isArray(v) ? v : [];
};
export const firstId = (record: CrmRecord | null | undefined, field: string): string | null => ids(record, field)[0] ?? null;

export const ACTIVE_CONTRACT_PHASES = ["Actief", "Verlenging", "Uittrede"];
export const OPEN_TICKET = (ticket: CrmRecord) => str(ticket, "Status") !== "Afgesloten";
export const OPEN_TASK = (task: CrmRecord) => str(task, "Status") !== "Klaar";

/** De FurniFlats-score (§4.4): gewogen gemiddelde van 6 checks. Zelfde weging als de backend. */
export const CHECKLIST = [
  { field: "Check: Foto's", label: "Professionele foto's", weight: 15, detail: "Goedgekeurde selectie in de Drive-map" },
  { field: "Check: Inventaris", label: "Inventaris compleet", weight: 15, detail: "Meubilair, keuken en linnen geregistreerd" },
  { field: "Check: Internet", label: "Internet, nuts en sleutels", weight: 20, detail: "Getest en geregistreerd" },
  { field: "Check: Documentatie", label: "Documentatie compleet", weight: 20, detail: "Overeenkomst, verzekering, handleidingen" },
  { field: "Check: Meubilair", label: "Meubilair volgens standaard", weight: 20, detail: "FurniFlats-inrichtingsstandaard" },
  { field: "Check: EPC", label: "EPC aanwezig", weight: 10, detail: "Verplicht voor publicatie" }
] as const;
const CHECK_VALUE: Record<string, number> = { Voldoet: 1, Gedeeltelijk: 0.5, Ontbreekt: 0 };

export function propertyScore(property: CrmRecord | null | undefined) {
  const total = CHECKLIST.reduce((sum, item) => sum + item.weight, 0);
  const earned = CHECKLIST.reduce((sum, item) => sum + item.weight * (CHECK_VALUE[str(property, item.field)] ?? 0), 0);
  return Math.round((earned / total) * 100);
}

/** Het team in de keuzelijsten (A9: vrije tekst, geen accounts). */
export const TEAM = ["Evy", "Koen", "Flor"];

/** Stand van een regel in een checklist: in orde, aandacht, ontbreekt. */
export type CheckState = "ok" | "warn" | "missing";

export type Tone = "neutral" | "green" | "teal" | "yellow" | "blue" | "purple" | "red" | "blush";

/** Kleur van een statusbadge per waarde. */
export const STATUS_TONE: Record<string, Tone> = {
  Verhuurd: "green", Beschikbaar: "teal", Uittrede: "yellow", "Check-in": "blue", Onboarding: "purple", "In optie": "yellow", "Pandbezoek gepland": "blue",
  Open: "yellow", Actief: "green", Urgent: "red", Hoog: "red", Spoed: "red", Gepland: "blue", Klaar: "green", Wacht: "purple",
  "Te beoordelen": "yellow", "Wacht op eigenaar": "purple", "Partner toegewezen": "blue", Afgesloten: "green",
  Nieuw: "blush", Contact: "blue", Intake: "purple", "Follow-up": "yellow", Nurture: "teal",
  Verlenging: "blue", Ondertekenen: "yellow", Gegevens: "yellow", Contract: "blue", Controle: "blue", Waarborg: "purple", Voorbereiding: "blue",
  Ontbreekt: "red", Opgevraagd: "yellow", Ontvangen: "green", Ondertekend: "green", Actueel: "green",
  "Te ontvangen": "yellow", Terugbetaald: "green", "Terugbetaling gestart": "blue", "Gedeeltelijke inhouding": "yellow", Betwist: "red"
};
